import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tagsAPI } from '../../services/api';

// Single async thunk to fetch tags (handles both global limit:1000 and paginated params)
export const fetchTags = createAsyncThunk(
  'tags/fetchTags',
  async (params, { rejectWithValue }) => {
    try {
      const response = await tagsAPI.getAll(params);
      return response; // { data: [...], pagination: {...} } OR { data: [...] } depending on API response structure
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createTag = createAsyncThunk(
  'tags/createTag',
  async (tagData, { rejectWithValue }) => {
    try {
      const response = await tagsAPI.create(tagData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTag = createAsyncThunk(
  'tags/updateTag',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await tagsAPI.update(id, data);
      return response.data || { ...data, id }; 
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteTag = createAsyncThunk(
  'tags/deleteTag',
  async (id, { rejectWithValue }) => {
    try {
      await tagsAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const bulkDeleteTags = createAsyncThunk(
  'tags/bulkDeleteTags',
  async (data, { rejectWithValue }) => {
    try {
      await tagsAPI.bulkDelete(data);
      return data.ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState: {
    items: [],
    total: 0,
    status: 'idle',
    error: null,
  },
  reducers: {
    clearTags: (state) => {
      state.items = [];
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tags
      .addCase(fetchTags.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Handle response structure depending on whether it's paginated or not
        if (action.payload.data) {
           state.items = action.payload.data;
           state.total = action.payload.pagination?.total || action.payload.data.length;
        } else {
           // Fallback if response is just array (unlikely with current API but safe to have)
           state.items = Array.isArray(action.payload) ? action.payload : [];
           state.total = state.items.length;
        }
        state.error = null;
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Create Tag
      .addCase(createTag.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.total += 1;
      })

      // Update Tag
      .addCase(updateTag.fulfilled, (state, action) => {
        const index = state.items.findIndex(tag => tag.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })

      // Delete Tag
      .addCase(deleteTag.fulfilled, (state, action) => {
        state.items = state.items.filter(tag => tag.id !== action.payload);
        state.total -= 1;
      })

      // Bulk Delete Tags
      .addCase(bulkDeleteTags.fulfilled, (state, action) => {
        if (action.payload && Array.isArray(action.payload)) {
             state.items = state.items.filter(tag => !action.payload.includes(tag.id));
             state.total -= action.payload.length;
        }
      });
  },
});

export const { clearTags } = tagsSlice.actions;

// Selectors
export const selectAllTags = (state) => state.tags.items;
export const selectTagsStatus = (state) => state.tags.status;
export const selectTagsError = (state) => state.tags.error;
export const selectTagsTotal = (state) => state.tags.total;

export default tagsSlice.reducer;
